/* Coverage-heatmap helpers and summary UI shared across authoring surface. */

// pct thresholds tuned for a 200-Q target: red until 25%, amber 25-75%, green 75%+
export function heatClass(pct) {
  if (pct >= 100) return "heat heat-full";
  if (pct >= 75) return "heat heat-green";
  if (pct >= 25) return "heat heat-amber";
  return "heat heat-red";
}

export function heatColor(pct) {
  if (pct >= 100) return "#3dc0af";
  if (pct >= 75) return "#3dc0af";
  if (pct >= 25) return "#f0be3c";
  return "#ef6a6a";
}

export function CoverageSummary({ course }) {
  if (!course) return null;
  const real = course._exam_pool_real ?? 0;
  const target = course._exam_pool_target ?? 200;
  const aiDrafts = course._exam_pool_ai_drafted ?? 0;
  const authorPending = course._exam_pool_author_pending ?? 0;
  // Prefer the authoritative server-computed count when present; fall back to
  // the derived total-minus-parts calculation (clamped to zero) so older API
  // responses that predate the fix don't render negative numbers.
  const placeholders = course._exam_pool_placeholders ??
    Math.max(0, (course._exam_pool_total ?? 0) - real - aiDrafts - authorPending);
  const pct = course._coverage_pct ?? 0;
  const remaining = Math.max(0, target - real);
  return (
    <div style={{ marginTop: 16, padding: 14, border: "1px solid #1c2a27", borderRadius: 4, background: "#08110f" }}
      data-testid="authoring-coverage-summary">
      <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
        color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
        Exam Pool Coverage
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ color: heatColor(pct), font: "800 20px 'Montserrat','Geist','Inter'" }} data-testid="authoring-coverage-pct">
          {pct}%
        </span>
        <span style={{ color: "#7b8b87", font: "500 11px 'JetBrains Mono', monospace" }}>
          {real} / {target} approved
        </span>
      </div>
      <div className="tbg-heat-bar">
        <span className={heatClass(pct)} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div style={{ marginTop: 8, font: "500 10px 'JetBrains Mono', monospace", color: "#7b8b87", lineHeight: 1.5 }}>
        {remaining === 0
          ? "Target reached. Course is production-ready."
          : `${remaining} more approved question${remaining === 1 ? "" : "s"} to hit target.`}
      </div>
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6, font: "500 10px 'JetBrains Mono', monospace" }}>
        <span style={{ color: "#3dc0af", padding: "3px 8px", border: "1px solid rgba(61,192,175,0.4)", borderRadius: 3 }}
          data-testid="authoring-count-approved">
          {real} approved
        </span>
        {authorPending > 0 && (
          <span style={{ color: "#6db8ff", padding: "3px 8px", border: "1px solid rgba(109,184,255,0.4)", borderRadius: 3 }}
            data-testid="authoring-count-author-pending">
            {authorPending} author pending
          </span>
        )}
        <span style={{ color: "#9c6cf8", padding: "3px 8px", border: "1px solid rgba(156,108,248,0.4)", borderRadius: 3 }}
          data-testid="authoring-count-ai-drafted">
          {aiDrafts} AI drafts
        </span>
        <span style={{ color: "#5a6a67", padding: "3px 8px", border: "1px solid #1c2a27", borderRadius: 3 }}
          data-testid="authoring-count-placeholder">
          {placeholders} placeholder
        </span>
      </div>
      <div style={{ marginTop: 8, font: "500 10px 'JetBrains Mono', monospace", color: "#5a6a67", lineHeight: 1.5 }}>
        Only approved questions reach students and count toward target.
      </div>
    </div>
  );
}

export function SummaryChip({ color, label, count }) {
  return (
    <div style={{
      padding: "6px 12px", border: `1px solid ${color}55`, borderRadius: 4,
      background: `${color}0d`,
    }}>
      <div style={{ font: "800 18px 'Montserrat','Geist','Inter'", color, lineHeight: 1 }}>{count}</div>
      <div style={{ font: "600 9px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
        color: "#7b8b87", textTransform: "uppercase", marginTop: 3 }}>{label}</div>
    </div>
  );
}
